import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import LoginLayout from 'components/system/layouts/LoginLayout';
import ScreenContext from 'Contexts/Common/ScreenContext';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import OutlineTextBox from 'components/system/atoms/textBoxs/OutlineTextBox';
import DoneButton from 'components/system/atoms/buttons/DoneButton';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';
import redirectLinks from 'constants/redirectLinks';
import LoginLogo from 'components/login/layouts/loginLogo';
import prizeMachineImage from 'assets/images/machine.png';
import { ConfirmDialog, InfoDialog } from 'components/system/organisms';
import MESSAGES from 'constants/messages';
import { handleCallFrontError, setScreenId } from 'redux/slices/commonSlice';
import MessageHelper from 'utils/message.helper';
/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  contens: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '111px 48px 23px 48px',
    '& .leftArea': {
      width: '436px',
      '& .systemLogo': {
        height: '50px',
        marginTop: 23,
      },
      '& .systemImage': {
        position: 'absolute',
        width: 652,
        top: 198,
        left: -80,
        zIndex: 0,
      },
    },
    '& .rightArea': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '60%',
      height: '586px',
      padding: '20px 48px 80px 48px',
      background: '#E5E5E5',
      borderRadius: '8px',
      zIndex: 1,
      '& .title': {
        fontSize: '40px',
      },
      '& .subTitle': {
        marginTop: '24px',
        fontSize: '22px',
      },
      '& .description': {
        margin: '16px 0 32px 0',
        fontSize: '21px',
      },
      '& .accountId' :{
        marginTop: '24px',
        fontSize: '24px',
        textAlign: 'left',
        width: '560px',
      }
    },
  },
  inputArea: {
    width: '560px',
    position: 'relative',
  },
  textBox: {
    marginTop: '24px',
    '& .outlinedInput': {
      height: '60px',
    },
  },
  doneBtn: {
    height: '60px',
    marginTop: '70px',
    marginBottom: '24px',
    fontSize: '28px',
    width: '560px',
  },
  linkBtn: {
    height: '60px',
    fontSize: '28px',
    width: '560px',
    backgroundColor: '#8E8E8E',
    '&:hover': {
      backgroundColor: '#8E8E8E', // ホバー時も同じ背景色に設定
    },
  },
}));
/* ************ Context ************ */
const ScreenContextValue = {
  title: 'ワンタイムパスワード設定',
  screenId: 'SCR013',
  currentMenu: '',
};
/* ************ Component ************ */
const Login: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  /* ************ state/redux ************ */
  // 画面項目用
  const [inputValue, setInputValue] = useState<string>("");
  const [isResend, setIsResend] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [versionNo, setVersionNo] = useState<number>(0);
  const userInfo = useAppSelector((state) => state.common.userInfo);
  const isInitialized = useRef(false);
  
  /* ************ Event ************ */
  // 画面項目のチェンジイベント
  const handleChangeInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(ev.target.value);
  };

  /* ************ useEffect ************ */
  useEffect(() => {
    if (!isInitialized.current) {
      createPassword();
      isInitialized.current = true; // 初回実行済みフラグを設定
    }
    dispatch(setScreenId('SCR013'));
  }, []);


  /* ************ API ************ */
  // ワンタイムパスワード発行API
  const createPassword = async () => {
    const userId = userInfo?.userId;
    const http = new HttpConnection({ dispatch });
        await http.post<boolean>(REST_API.LOGIN.ONE_TIME_LOGIN, {userId: userId})
    getVersionApi();
  };

  // ワンタイムパスワード確認API
  const handleClickDone = async () => {
    // パスワードが空の場合はエラー
    if (inputValue === '') {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MESSAGES.INF120)] },}),);
      return; // バリデーションエラーの場合は false を返す
    }
    const userId = userInfo?.userId;
    const http = new HttpConnection({ dispatch });
        await http
          .post<boolean>(REST_API.LOGIN.SEND_ONE_TIME_CODE, {userId: userId, code: inputValue, versionNo: versionNo})
          .then((data) => {
              if (data) {
                dispatch(setScreenId(''));
                navigate(redirectLinks.HOME_TOP);
              }
          });
  };

    // userテーブルのversionNoの取得
    const getVersionApi = async () => {
      const http = new HttpConnection({ dispatch });
      const data = await http.get<number>(REST_API.LOGIN.GET_USER_VERSION_NO, {userId: userInfo?.userId});
      if (data) {
        setVersionNo(data);
      }
    };

  // 再送信する際の確認チェック
  const resendPassword = () => {
    setIsSuccess(true);
    setIsResend(false);
    createPassword();
    getVersionApi();
  }
  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <LoginLayout>
        <Box className={classes.contens}>
          <Box className="leftArea">
            <Box className="systemLogo">
              <LoginLogo />
            </Box>
            <Box className="systemImage">
              <img src={prizeMachineImage} alt="プライズ機イメージ" />
            </Box>
          </Box>
          <Box className="rightArea">
            <div className="title">ワンタイムパスワード認証</div>
            <div className='subTitle'>お送りしたメール内に記載されているワンタイムパスワードを入力してください。</div>
            <div className='accountId'>アカウントID：{userInfo?.accountId}</div>
            <Box className={classes.inputArea}>
              <OutlineTextBox
                id="code"
                name="code"
                value={inputValue}
                placeholder="ワンタイムパスワード"
                type='text'
                onChange={handleChangeInput}
                className={classes.textBox}
                fullWidth
              />
              {/* <Box className={classes.linkBtn} onClick={() => setIsResend(true)}>ワンタイムパスワードを再送する</Box> */}
            </Box>
            <DoneButton className={classes.doneBtn} onClick={handleClickDone} fullWidth>
              認証
            </DoneButton>
            <DoneButton className={classes.linkBtn} onClick={() => setIsResend(true)} fullWidth>
              ワンタイムパスワード再発行
            </DoneButton>
          </Box>
        </Box>
        <ConfirmDialog
          open={isResend}
          msg={MESSAGES.INF257}
          onOk={() => resendPassword()}
          onCancel={() => setIsResend(false)}
        />
        <InfoDialog open={isSuccess} closeFunc={() => setIsSuccess(false)} msg={MESSAGES.INF258} />
      </LoginLayout>
    </ScreenContext.Provider>
  );
};
export default Login;